import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product, CartItem } from '@/types/product';
import { toast } from '@/hooks/use-toast';
import { products } from '@/data/products';
import { WishListVenueType,VenuesInterface } from '@/types/venueInterfaceType';


interface WishlistItem {
  venueId: string;
}

interface ShoppingState {
  cartItems: CartItem[];
  wishlistItems:VenuesInterface[];
}

// Get initial state from localStorage
const getInitialState = (): ShoppingState => {
  if (typeof window === 'undefined') {
    return {
      cartItems: [],
      wishlistItems: []
    };
  }
  
  return {
    cartItems: JSON.parse(localStorage.getItem('cart') || '[]'),
    wishlistItems: JSON.parse(localStorage.getItem('wishlist') || '[]'),
  };
};

const initialState: ShoppingState = getInitialState();

export const shoppingSlice = createSlice({
  name: 'shopping',
  initialState,
  reducers: {
    addToCart: (
      state, 
      action: PayloadAction<{
        product: Product;
        quantity: number;
        isRental: boolean;
        rentalDays?: number;
      }>
    ) => {
      const { product, quantity, isRental, rentalDays } = action.payload;
      
      // Check if item already exists in cart
      const existingItemIndex = state.cartItems.findIndex(
        item => item.productId === product.id && item.isRental === isRental
      );
      
      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        state.cartItems[existingItemIndex].quantity += quantity;
        toast({
          title: "Cart updated",
          description: `${product.name} quantity updated in your cart.`,
        });
      } else {
        // Add new item
        state.cartItems.push({ 
          productId: product.id, 
          quantity, 
          isRental,
          ...(isRental && rentalDays ? { rentalDays } : {})
        });
        toast({
          title: "Added to cart",
          description: `${product.name} has been added to your cart.`,
        });
      }
      
      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(state.cartItems));
    },
    
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cartItems = state.cartItems.filter(item => item.productId !== action.payload);
      toast({
        title: "Item removed",
        description: "The item has been removed from your cart.",
      });
      
      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(state.cartItems));
    },
    
    updateCartItemQuantity: (
      state, 
      action: PayloadAction<{ productId: string; quantity: number }>
    ) => {
      const { productId, quantity } = action.payload;
      const itemIndex = state.cartItems.findIndex(item => item.productId === productId);
      
      if (itemIndex !== -1) {
        state.cartItems[itemIndex].quantity = quantity;
        // Save to localStorage
        localStorage.setItem('cart', JSON.stringify(state.cartItems));
      }
    },
    
    updateCartItemRentalDays: (
      state, 
      action: PayloadAction<{ productId: string; days: number }>
    ) => {
      const { productId, days } = action.payload;
      const itemIndex = state.cartItems.findIndex(
        item => item.productId === productId && item.isRental
      );
      
      if (itemIndex !== -1) {
        state.cartItems[itemIndex].rentalDays = days;
        // Save to localStorage
        localStorage.setItem('cart', JSON.stringify(state.cartItems));
      }
    },
    
    clearCart: (state) => {
      state.cartItems = [];
      toast({
        title: "Cart cleared",
        description: "All items have been removed from your cart.",
      });
      
      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(state.cartItems));
    },
    
    addToWishlist: (state, action: PayloadAction<VenuesInterface>) => {
      const Venue = action.payload;
      // Check if item already exists in wishlist
      const exists = state.wishlistItems.some(item => item._id === Venue._id);
      
      if (exists) {
        // toast({
        //   title: "Already in wishlist",
        //   description: `Venue is already in your wishlist.`,
        // });
      } else {
        state.wishlistItems= [...state.wishlistItems,Venue];
        // toast({
        //   title: "Added to wishlist",
        //   description: `Venue has been added to your wishlist.`,
        // });
      }
      
      // Save to localStorage
      localStorage.setItem('wishlist', JSON.stringify(state.wishlistItems));
    },
    
    removeFromWishlist: (state, action: PayloadAction<string>) => {
      state.wishlistItems = state.wishlistItems.filter(ele => ele._id !== action.payload);
      // toast({
      //   title: "Removed from wishlist",
      //   description: "The item has been removed from your wishlist.",
      // })
      // Save to localStorage
      localStorage.setItem('wishlist', JSON.stringify(state.wishlistItems));
    },
    
    addSampleItemsToCart: (state) => {
      // Get a few sample products
      const sampleProducts = [
        products[0], // Premium Wireless Headphones
        products[2], // Smart Fitness Watch
        products[4]  // Portable Bluetooth Speaker
      ];
      
      // Clear the existing cart first
      state.cartItems = [];
      
      // Add first product as a regular purchase
      state.cartItems.push({
        productId: sampleProducts[0].id,
        quantity: 1,
        isRental: false
      });
      
      // Add second product as a rental
      if (sampleProducts[1].forRent) {
        state.cartItems.push({
          productId: sampleProducts[1].id,
          quantity: 1,
          isRental: true,
          rentalDays: 3
        });
      } else {
        state.cartItems.push({
          productId: sampleProducts[1].id,
          quantity: 1,
          isRental: false
        });
      }
      
      // Add third product with higher quantity
      state.cartItems.push({
        productId: sampleProducts[2].id,
        quantity: 2,
        isRental: false
      });
      
      // Save to localStorage
      localStorage.setItem('cart', JSON.stringify(state.cartItems));
      
      toast({
        title: "Sample items added",
        description: "Sample items have been added to your cart for demonstration.",
      });
    }
  }
});

export const { 
  addToCart, 
  removeFromCart, 
  updateCartItemQuantity, 
  updateCartItemRentalDays, 
  clearCart, 
  addToWishlist, 
  removeFromWishlist, 
  addSampleItemsToCart 
} = shoppingSlice.actions;

// Selectors
export const selectCartItems = (state: { shopping: ShoppingState }) => state.shopping.cartItems;
export const selectWishlistItems = (state: { shopping: ShoppingState }) => state.shopping.wishlistItems;
export const selectCartItemCount = (state: { shopping: ShoppingState }) => 
  state.shopping.cartItems.reduce((total, item) => total + item.quantity, 0);
export const selectWishlistItemCount = (state: { shopping: ShoppingState }) => 
  state.shopping.wishlistItems.length;
export const selectIsInCart = (state: { shopping: ShoppingState }, productId: string) => 
  state.shopping.cartItems.some(item => item.productId === productId);
export const selectIsInWishlist = (state: { shopping: ShoppingState }, productId: string) => 
  state.shopping.wishlistItems.some(item => item._id === productId);

export default shoppingSlice.reducer;
